"use client";

import {
  DndContext,
  type DragEndEvent,
  type DragMoveEvent,
  DragOverlay,
  type DragStartEvent,
  PointerSensor,
  pointerWithin,
  TouchSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  ArrowUpDown,
  ChevronDown,
  ChevronUp,
  Facebook,
  GripVertical,
  Heading,
  ImageIcon,
  Instagram,
  MousePointerClick,
  Plus,
  RotateCcw,
  Sparkles,
  TextIcon,
  Trash2,
  Youtube,
} from "lucide-react";
import Image from "next/image";
import { useCallback, useId, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import { SectionHeading } from "../../home/section";

/* ------------------------------------------------------------------ */
/* Brand assets (Fitt Pilates) — mirrors the in-app email rendering    */
/* ------------------------------------------------------------------ */

const FITT_LOGO_WIDE = "/images/website/fitt-pilates-logo-wide.webp";
const FITT_LOGO_LIGHT_WIDE = "/images/website/fitt-pilates-logo-light-wide.png";
const FITT_STUDIO = "/images/website/fitt-sudio.jpg";

/* ------------------------------------------------------------------ */
/* Block model — a slimmed-down version of the product's email blocks  */
/* ------------------------------------------------------------------ */

type BlockKind = "heading" | "text" | "button" | "image" | "divider" | "spacer";

interface DemoBlock {
  id: string;
  kind: BlockKind;
}

interface PaletteDef {
  kind: BlockKind;
  label: string;
  icon: typeof Heading;
  hint: string;
}

const PALETTE: PaletteDef[] = [
  { kind: "heading", label: "Heading", icon: Heading, hint: "A bold headline" },
  { kind: "text", label: "Text", icon: TextIcon, hint: "A paragraph of copy" },
  { kind: "image", label: "Image", icon: ImageIcon, hint: "A full-width photo" },
  { kind: "button", label: "Button", icon: MousePointerClick, hint: "A call to action" },
  { kind: "divider", label: "Divider", icon: ArrowUpDown, hint: "A thin rule" },
  { kind: "spacer", label: "Spacer", icon: Plus, hint: "Vertical space" },
];

const ESTIMATED_HEIGHTS: Record<BlockKind, number> = {
  heading: 30,
  text: 60,
  button: 44,
  image: 150,
  divider: 16,
  spacer: 24,
};

/* ------------------------------------------------------------------ */
/* Drop-index math — mirrors the in-product builder exactly            */
/* ------------------------------------------------------------------ */

function computeDropIndex(
  canvasEl: HTMLElement | null,
  pointerY: number,
  activeBlockIndex: number | null,
): number {
  if (!canvasEl) return 0;
  const blockEls = canvasEl.querySelectorAll<HTMLElement>("[data-block-index]");
  for (const el of blockEls) {
    const idx = Number(el.dataset.blockIndex);
    if (activeBlockIndex !== null && idx === activeBlockIndex) continue;
    const rect = el.getBoundingClientRect();
    const midpoint = rect.top + rect.height / 2;
    if (pointerY < midpoint) return idx;
  }
  return blockEls.length;
}

/* ------------------------------------------------------------------ */
/* Rendered block content — matches how Helios renders email blocks    */
/* ------------------------------------------------------------------ */

function BlockContent({ kind }: { kind: BlockKind }) {
  switch (kind) {
    case "heading":
      return (
        <h3 className="text-[20px] font-bold leading-tight text-[#1f2937] dark:text-white">
          We just opened on South Congress
        </h3>
      );
    case "text":
      return (
        <p className="text-[14px] leading-[1.55] text-[#374151] dark:text-[#c7c7cc]">
          Join us Saturday, June 28 for free classes all weekend, plus founding-member pricing for
          the first 100 to sign up.
        </p>
      );
    case "button":
      return (
        <span className="inline-flex items-center rounded-md bg-[#007AFF] px-7 py-2.5 text-[14px] font-semibold text-white">
          Reserve your free class
        </span>
      );
    case "image":
      return (
        <Image
          src={FITT_STUDIO}
          alt="Inside the new Fitt Pilates studio"
          width={1200}
          height={800}
          className="h-[150px] w-full rounded-[6px] object-cover"
          draggable={false}
        />
      );
    case "divider":
      return <div className="h-px w-full bg-[#e5e7eb] dark:bg-[#38383a]" />;
    case "spacer":
      return <div className="h-6 w-full" />;
  }
}

/* ------------------------------------------------------------------ */
/* Palette item — draggable AND tap-to-add (first-class on every device) */
/* ------------------------------------------------------------------ */

function PaletteItem({
  def,
  onAdd,
  dragging,
}: {
  def: PaletteDef;
  onAdd: (kind: BlockKind) => void;
  dragging: boolean;
}) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `palette-${def.kind}`,
    data: { source: "palette", kind: def.kind },
  });
  const Icon = def.icon;

  return (
    <button
      ref={setNodeRef}
      type="button"
      onClick={() => onAdd(def.kind)}
      className={cn(
        "group/item flex shrink-0 cursor-grab touch-none select-none items-center gap-2.5 rounded-lg border border-border bg-background px-3 py-2.5 text-left transition-all active:cursor-grabbing",
        "hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-md dark:hover:border-brand-accent/40",
        "lg:w-full",
        isDragging && "opacity-40",
        dragging && !isDragging && "opacity-60",
      )}
      {...attributes}
      {...listeners}
    >
      <span className="flex size-8 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground transition-colors group-hover/item:bg-primary/10 group-hover/item:text-primary dark:group-hover/item:bg-brand-accent/10 dark:group-hover/item:text-brand-accent">
        <Icon className="size-4" />
      </span>
      <span className="min-w-0">
        <span className="block text-[13px] font-semibold leading-tight text-foreground">
          {def.label}
        </span>
        <span className="hidden text-[11px] leading-tight text-muted-foreground lg:block">
          {def.hint}
        </span>
      </span>
      <GripVertical className="ml-auto hidden size-4 shrink-0 text-muted-foreground/40 transition-colors group-hover/item:text-muted-foreground lg:block" />
    </button>
  );
}

/* ------------------------------------------------------------------ */
/* Canvas block — whole surface draggable, floating toolbar on hover   */
/* ------------------------------------------------------------------ */

function CanvasBlock({
  block,
  index,
  total,
  isPlaceholder,
  onRemove,
  onMove,
}: {
  block: DemoBlock;
  index: number;
  total: number;
  isPlaceholder: boolean;
  onRemove: (id: string) => void;
  onMove: (from: number, to: number) => void;
}) {
  const { attributes, listeners, setNodeRef } = useDraggable({
    id: block.id,
    data: { source: "canvas", index },
  });

  return (
    <div
      ref={setNodeRef}
      data-block-index={index}
      style={{ userSelect: "none", WebkitUserSelect: "none" }}
      className={cn(
        "group/block relative cursor-grab rounded-md border-2 border-transparent px-2 py-1 outline-none transition-colors active:cursor-grabbing",
        "hover:border-primary/40 dark:hover:border-brand-accent/40",
        isPlaceholder && "opacity-30",
      )}
      {...attributes}
      {...listeners}
    >
      {/* Floating toolbar — appears on hover (pointer) / always (touch) */}
      <div
        className={cn(
          "absolute -top-px right-2 z-10 flex -translate-y-full items-center gap-px rounded-t-md border border-b-0 border-border bg-card px-0.5 py-0.5 shadow-sm transition-opacity",
          "opacity-100 lg:opacity-0 lg:group-hover/block:opacity-100",
        )}
      >
        <span className="flex cursor-grab items-center p-1 text-muted-foreground active:cursor-grabbing">
          <GripVertical className="size-3" />
        </span>
        <ToolbarButton
          label="Move up"
          disabled={index === 0}
          onClick={(e) => {
            e.stopPropagation();
            onMove(index, index - 1);
          }}
        >
          <ChevronUp className="size-3" />
        </ToolbarButton>
        <ToolbarButton
          label="Move down"
          disabled={index === total - 1}
          onClick={(e) => {
            e.stopPropagation();
            onMove(index, index + 1);
          }}
        >
          <ChevronDown className="size-3" />
        </ToolbarButton>
        <ToolbarButton
          label="Delete block"
          destructive
          onClick={(e) => {
            e.stopPropagation();
            onRemove(block.id);
          }}
        >
          <Trash2 className="size-3" />
        </ToolbarButton>
      </div>

      <BlockContent kind={block.kind} />
    </div>
  );
}

function ToolbarButton({
  children,
  label,
  onClick,
  disabled,
  destructive,
}: {
  children: React.ReactNode;
  label: string;
  onClick: (e: React.MouseEvent) => void;
  disabled?: boolean;
  destructive?: boolean;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      disabled={disabled}
      onPointerDown={(e) => e.stopPropagation()}
      onClick={onClick}
      className={cn(
        "rounded p-1 text-muted-foreground transition-colors disabled:opacity-30",
        destructive
          ? "hover:bg-destructive/10 hover:text-destructive"
          : "hover:bg-muted hover:text-foreground disabled:hover:bg-transparent disabled:hover:text-muted-foreground",
      )}
    >
      {children}
    </button>
  );
}

/* ------------------------------------------------------------------ */
/* Drop indicator — the line that follows the cursor                   */
/* ------------------------------------------------------------------ */

function DropIndicator({ height }: { height: number }) {
  return (
    <div className="px-2 py-1">
      <div className="h-[3px] rounded-full bg-primary dark:bg-brand-accent" />
      <div
        className="mt-1 flex items-center justify-center rounded-md border-2 border-dashed border-primary/30 bg-primary/[0.04] dark:border-brand-accent/30 dark:bg-brand-accent/[0.06]"
        style={{ height: `${Math.max(height, 32)}px` }}
      >
        <span className="text-[11px] font-medium text-primary/60 dark:text-brand-accent/70">
          Drop here
        </span>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Empty state — the obvious, enticing invitation to play              */
/* ------------------------------------------------------------------ */

function EmptyDropzone({ active }: { active: boolean }) {
  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center gap-3 rounded-lg border-2 border-dashed px-6 py-14 text-center transition-colors",
        active
          ? "border-primary/60 bg-primary/[0.05] dark:border-brand-accent/60 dark:bg-brand-accent/[0.06]"
          : "border-border bg-muted/30",
      )}
    >
      <span
        className={cn(
          "flex size-12 items-center justify-center rounded-full transition-colors",
          active
            ? "bg-primary/15 text-primary dark:bg-brand-accent/15 dark:text-brand-accent"
            : "animate-pulse bg-primary/10 text-primary dark:bg-brand-accent/10 dark:text-brand-accent",
        )}
      >
        <Plus className="size-6" />
      </span>
      <div>
        <p className="text-sm font-semibold text-foreground">
          {active ? "Drop it right here" : "Start building your email"}
        </p>
        <p className="mt-1 text-[13px] leading-relaxed text-muted-foreground">
          Drag a block in from the <span className="lg:hidden">strip above</span>
          <span className="hidden lg:inline">left</span> — or just tap one to add it.
        </p>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Static email chrome (header + footer) — fixed Fitt brand            */
/* ------------------------------------------------------------------ */

function EmailHeader() {
  return (
    <div className="flex items-center justify-between gap-4 px-6 pt-6">
      <Image
        src={FITT_LOGO_LIGHT_WIDE}
        alt="Fitt Pilates"
        width={1500}
        height={471}
        className="h-8 w-auto object-contain dark:hidden"
        draggable={false}
      />
      <Image
        src={FITT_LOGO_WIDE}
        alt="Fitt Pilates"
        width={1500}
        height={471}
        className="hidden h-8 w-auto object-contain dark:block"
        draggable={false}
      />
      <div className="flex items-center gap-3.5 text-[#9ca3af] dark:text-[#8e8e93]">
        <Instagram className="size-[18px]" />
        <Facebook className="size-[18px]" />
        <Youtube className="size-[18px]" />
      </div>
    </div>
  );
}

function EmailFooter() {
  return (
    <div className="px-6 pb-7 pt-2">
      <div className="mb-5 h-px bg-[#e5e7eb] dark:bg-[#38383a]" />
      <p className="text-[13px] font-bold text-[#374151] dark:text-[#e5e5ea]">Fitt Pilates</p>
      <p className="mt-1 text-[12px] text-[#6b7280] dark:text-[#98989d]">
        1500 South Congress Ave, Austin, TX
      </p>
      <p className="mt-3 text-[11px] leading-relaxed text-[#9ca3af] dark:text-[#8e8e93]">
        You are receiving this email because you opted in to updates from Fitt Pilates.
      </p>
      <p className="mt-1 text-[11px] text-[#9ca3af] dark:text-[#8e8e93]">
        <span className="underline">Unsubscribe</span>
      </p>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Email body — the drop target                                        */
/* ------------------------------------------------------------------ */

function EmailBody({
  blocks,
  isDragging,
  overIndex,
  activeId,
  dropHeight,
  canvasRef,
  onRemove,
  onMove,
}: {
  blocks: DemoBlock[];
  isDragging: boolean;
  overIndex: number | null;
  activeId: string | null;
  dropHeight: number;
  canvasRef: React.RefObject<HTMLDivElement | null>;
  onRemove: (id: string) => void;
  onMove: (from: number, to: number) => void;
}) {
  const { setNodeRef } = useDroppable({ id: "email-body" });
  const composedRef = useCallback(
    (node: HTMLDivElement | null) => {
      setNodeRef(node);
      canvasRef.current = node;
    },
    [setNodeRef, canvasRef],
  );

  const empty = blocks.length === 0;

  return (
    <div ref={composedRef} className="px-3 pb-2 pt-4">
      {empty ? (
        <EmptyDropzone active={isDragging && overIndex !== null} />
      ) : (
        <div>
          {blocks.map((block, index) => (
            <div key={block.id}>
              {isDragging && overIndex === index && <DropIndicator height={dropHeight} />}
              <CanvasBlock
                block={block}
                index={index}
                total={blocks.length}
                isPlaceholder={activeId === block.id}
                onRemove={onRemove}
                onMove={onMove}
              />
            </div>
          ))}
          {isDragging && overIndex !== null && overIndex >= blocks.length && (
            <DropIndicator height={dropHeight} />
          )}
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Main interactive section                                            */
/* ------------------------------------------------------------------ */

interface DragState {
  activeId: string | null;
  source: "palette" | "canvas" | null;
  paletteKind: BlockKind | null;
  overIndex: number | null;
}

const IDLE: DragState = { activeId: null, source: null, paletteKind: null, overIndex: null };

export function EmailBuilderDemo() {
  const idPrefix = useId();
  const counter = useRef(0);
  const canvasRef = useRef<HTMLDivElement | null>(null);

  const makeId = useCallback(
    (kind: BlockKind) => {
      counter.current += 1;
      return `${idPrefix}-${kind}-${counter.current}`;
    },
    [idPrefix],
  );

  const [blocks, setBlocks] = useState<DemoBlock[]>([]);
  const [drag, setDrag] = useState<DragState>(IDLE);
  // Once the visitor adds or drags a block, the "try it" hint has done its job.
  const [hasInteracted, setHasInteracted] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 180, tolerance: 8 } }),
  );

  const addBlock = useCallback(
    (kind: BlockKind, index?: number) => {
      setHasInteracted(true);
      setBlocks((curr) => {
        const next = [...curr];
        next.splice(index ?? next.length, 0, { id: makeId(kind), kind });
        return next;
      });
    },
    [makeId],
  );

  const removeBlock = useCallback((id: string) => {
    setBlocks((curr) => curr.filter((b) => b.id !== id));
  }, []);

  const moveBlock = useCallback((from: number, to: number) => {
    setBlocks((curr) => {
      if (to < 0 || to >= curr.length) return curr;
      const next = [...curr];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next;
    });
  }, []);

  const reset = useCallback(() => setBlocks([]), []);

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setHasInteracted(true);
    const data = event.active.data.current as
      | { source?: "palette" | "canvas"; kind?: BlockKind }
      | undefined;
    setDrag({
      activeId: String(event.active.id),
      source: data?.source ?? null,
      paletteKind: data?.source === "palette" ? (data.kind ?? null) : null,
      overIndex: null,
    });
  }, []);

  const handleDragMove = useCallback(
    (event: DragMoveEvent) => {
      const ae = event.activatorEvent;
      let baseY = 0;
      if (ae instanceof MouseEvent) baseY = ae.clientY;
      else if (typeof TouchEvent !== "undefined" && ae instanceof TouchEvent) {
        baseY = ae.touches[0]?.clientY ?? ae.changedTouches[0]?.clientY ?? 0;
      }
      const pointerY = baseY + (event.delta?.y ?? 0);

      setDrag((prev) => {
        const activeIndex =
          prev.source === "canvas" && prev.activeId
            ? blocks.findIndex((b) => b.id === prev.activeId)
            : null;
        const idx = computeDropIndex(canvasRef.current, pointerY, activeIndex);
        return prev.overIndex === idx ? prev : { ...prev, overIndex: idx };
      });
    },
    [blocks],
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const targetIndex = drag.overIndex;
      const data = event.active.data.current as
        | { source?: "palette" | "canvas"; kind?: BlockKind }
        | undefined;
      setDrag(IDLE);

      // No movement registered (e.g. dropped outside) — bail.
      if (targetIndex === null) return;

      if (data?.source === "palette" && data.kind) {
        addBlock(data.kind, targetIndex);
        return;
      }

      const oldIndex = blocks.findIndex((b) => b.id === event.active.id);
      if (oldIndex === -1) return;
      let newIndex = targetIndex;
      if (oldIndex < newIndex) newIndex -= 1;
      if (oldIndex !== newIndex) moveBlock(oldIndex, newIndex);
    },
    [drag.overIndex, blocks, addBlock, moveBlock],
  );

  const isDragging = drag.source !== null;
  const dropHeight = drag.paletteKind
    ? ESTIMATED_HEIGHTS[drag.paletteKind]
    : drag.source === "canvas" && drag.activeId
      ? (ESTIMATED_HEIGHTS[blocks.find((b) => b.id === drag.activeId)?.kind ?? "text"] ?? 48)
      : 48;

  return (
    <section className="border-b border-border bg-background pt-20 lg:pt-28">
      <div className="mx-auto max-w-7xl px-4 lg:px-8">
        <SectionHeading
          align="left"
          size="sm"
          eyebrow={
            <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary dark:text-brand-accent">
              <Sparkles className="size-4" />
              Try it yourself
            </span>
          }
          title={
            <>
              Build a real email right here.
              <span className="text-muted-foreground">
                {" "}
                Drag a block in, drop it exactly where you want, and watch it render like it will in
                the inbox.
              </span>
            </>
          }
        />
      </div>

      <div className="mt-10 border-t border-border bg-grid lg:mt-14">
        <div className="mx-auto max-w-7xl px-4 lg:px-8">
          <DndContext
            sensors={sensors}
            collisionDetection={pointerWithin}
            onDragStart={handleDragStart}
            onDragMove={handleDragMove}
            onDragEnd={handleDragEnd}
            onDragCancel={() => setDrag(IDLE)}
          >
            <div className="grid grid-cols-1 gap-px border-x border-border bg-border lg:grid-cols-[300px_1fr] [&>*]:bg-background">
              {/* Palette */}
              <div className="relative z-10 flex flex-col gap-4 px-5 py-6 lg:px-6 lg:py-8">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <h3 className="text-sm font-semibold text-foreground">Content blocks</h3>
                    <p className="mt-0.5 text-[12px] text-muted-foreground">
                      <span className="lg:hidden">Tap or drag to add</span>
                      <span className="hidden lg:inline">
                        Drag onto the email — or click to add
                      </span>
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={reset}
                    disabled={blocks.length === 0}
                    className="inline-flex shrink-0 items-center gap-1.5 rounded-md border border-border bg-background px-2.5 py-1.5 text-[12px] font-medium text-muted-foreground transition-colors hover:text-foreground disabled:cursor-default disabled:opacity-40 disabled:hover:text-muted-foreground"
                  >
                    <RotateCcw className="size-3.5" />
                    Reset
                  </button>
                </div>

                {!hasInteracted && <TryItCue />}

                <div className="flex gap-2 overflow-x-auto pb-1 lg:grid lg:grid-cols-1 lg:gap-2 lg:overflow-visible lg:pb-0">
                  {PALETTE.map((def) => (
                    <PaletteItem
                      key={def.kind}
                      def={def}
                      onAdd={(k) => addBlock(k)}
                      dragging={drag.source === "palette"}
                    />
                  ))}
                </div>
              </div>

              {/* Email canvas */}
              <div className="flex justify-center px-4 py-8 lg:px-8 lg:py-10">
                <div className="w-full max-w-[520px] self-start overflow-hidden rounded-xl border border-border bg-white shadow-xl shadow-black/5 dark:bg-background dark:shadow-black/30">
                  <EmailHeader />
                  <EmailBody
                    blocks={blocks}
                    isDragging={isDragging}
                    overIndex={drag.overIndex}
                    activeId={drag.source === "canvas" ? drag.activeId : null}
                    dropHeight={dropHeight}
                    canvasRef={canvasRef}
                    onRemove={removeBlock}
                    onMove={moveBlock}
                  />
                  <EmailFooter />
                </div>
              </div>
            </div>

            <DragOverlay dropAnimation={null}>
              {drag.source === "palette" && drag.paletteKind ? (
                <PaletteDragPreview kind={drag.paletteKind} />
              ) : drag.source === "canvas" && drag.activeId ? (
                <BlockDragPreview
                  kind={blocks.find((b) => b.id === drag.activeId)?.kind ?? "text"}
                />
              ) : null}
            </DragOverlay>
          </DndContext>
        </div>
      </div>
    </section>
  );
}

function PaletteDragPreview({ kind }: { kind: BlockKind }) {
  const def = PALETTE.find((d) => d.kind === kind);
  if (!def) return null;
  const Icon = def.icon;
  return (
    <div className="flex cursor-grabbing items-center gap-2.5 rounded-lg border border-primary/50 bg-background px-3 py-2.5 shadow-xl dark:border-brand-accent/50">
      <span className="flex size-8 items-center justify-center rounded-md bg-primary/10 text-primary dark:bg-brand-accent/10 dark:text-brand-accent">
        <Icon className="size-4" />
      </span>
      <span className="text-[13px] font-semibold text-foreground">{def.label}</span>
    </div>
  );
}

function BlockDragPreview({ kind }: { kind: BlockKind }) {
  return (
    <div className="w-[460px] max-w-[80vw] cursor-grabbing rounded-md border-2 border-primary bg-white px-2 py-1 shadow-2xl dark:border-brand-accent dark:bg-background">
      <BlockContent kind={kind} />
    </div>
  );
}

/**
 * Friendly hand-drawn note that sits just off the right edge of the block
 * panel, overlapping it, with the arrow curving back to point at the blocks.
 * Desktop only — on mobile the palette is a top strip, so "to the right"
 * doesn't apply (the empty dropzone carries the hint there instead).
 */
function TryItCue() {
  return (
    <div className="pointer-events-none absolute left-[13.5rem] top-[5rem] z-20 hidden items-center gap-1.5 text-primary lg:flex dark:text-brand-accent">
      <svg
        viewBox="0 0 64 48"
        className="h-12 w-14 shrink-0"
        fill="none"
        stroke="currentColor"
        strokeWidth={2.25}
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <title>Arrow pointing to the content blocks</title>
        <path d="M58 9C30 1 6 15 14 38" />
        <path d="M14 38l12-3M14 38l3-12" />
      </svg>
      <p className="-rotate-2 whitespace-nowrap font-handwritten text-[20px] font-semibold leading-[1.05]">
        Try it — drag a block
        <br />
        onto your email
      </p>
    </div>
  );
}
